import { useEffect, useRef, useState } from 'react';

import { useSoyuzClientConfig } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import debounce from '../../utils/debounce';

import { normalizeLocalityToCityId, findMatchedSubstring } from './utils';

// MapTiler has no autocomplete session-token concept: each keystroke is a
// separate billed geocoding request. The 300ms debounce mirrors the other
// providers; suggest responses already include full Feature data so there is
// no separate retrieve call.
const GEOCODING_URL = 'https://api.maptiler.com/geocoding';

const extractCityIdFromFeature = feature => {
  const placeContext = feature.context?.find(c => c.id?.startsWith('place.'));
  const localityName = placeContext?.text || feature.text || '';
  return normalizeLocalityToCityId(localityName);
};

// Build the secondary line shown under the main place name in the dropdown
// from MapTiler's `context` chain (locality → region → country). MapTiler's
// `place_name_<lang>` fields are not always populated for non-English locales,
// so deriving from `context` is more reliable across languages.
const buildSecondaryText = feature => {
  if (!Array.isArray(feature.context)) return '';
  return feature.context
    .map(c => c.text)
    .filter(Boolean)
    .join(', ');
};

const useAutocompleteService = ({
  countryCode,
  boundCoords,
  pointOfInterest,
  onPlaceSelected
}) => {
  const { maptilerApiKey } = useSoyuzClientConfig();
  const placesRef = useRef(null);
  const [inputValue, setInputValue] = useState(pointOfInterest?.name);
  const [suggestionList, setSuggestionList] = useState([]);
  const [suggestionDescriptions, setSuggestionDescriptions] = useState([]);
  const placesMapRef = useRef({});

  const closeSuggestions = e => {
    if (placesRef.current && !placesRef.current.contains(e.target)) {
      setSuggestionList([]);
      setSuggestionDescriptions([]);
    }
  };

  useEffect(() => {
    document.addEventListener('click', closeSuggestions);
    return () => document.removeEventListener('click', closeSuggestions);
  }, []);

  useEffect(() => {
    setInputValue(pointOfInterest ? pointOfInterest.name : '');
  }, [pointOfInterest]);

  const requestSuggestions = useRef(
    debounce(async value => {
      if (value.length <= 2 || !maptilerApiKey) {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
        return;
      }

      SoyuzAnalytics.sendGA4Event('map-places', {
        provider: 'maptiler',
        input: value
      });

      try {
        const params = new URLSearchParams({
          key: maptilerApiKey,
          autocomplete: 'true',
          limit: '5',
          language: document.documentElement.lang || 'en',
          types:
            'poi,address,place,locality,neighbourhood,postal_code,region,subregion,municipality'
        });

        if (boundCoords) {
          params.set(
            'proximity',
            `${parseFloat(boundCoords.lng)},${parseFloat(boundCoords.lat)}`
          );
        }

        if (countryCode) {
          params.set('country', countryCode);
        }

        const response = await fetch(
          `${GEOCODING_URL}/${encodeURIComponent(value)}.json?${params}`
        );
        const data = await response.json();

        const features = data?.features || [];
        if (features.length === 0) {
          placesMapRef.current = {};
          setSuggestionList([]);
          setSuggestionDescriptions([]);
          return;
        }

        const newPlacesMap = {};
        const formatted = features.map(feature => {
          const description = feature.place_name || feature.text;
          const mainText = feature.text || description;
          const secondaryText = buildSecondaryText(feature);
          newPlacesMap[description] = feature;
          return {
            maptiler_id: feature.id,
            description,
            place_id: feature.id,
            structured_formatting: {
              main_text: mainText,
              secondary_text: secondaryText,
              main_text_matched_substrings: [
                findMatchedSubstring(mainText, value)
              ],
              secondary_text_matched_substrings: [
                findMatchedSubstring(secondaryText, value)
              ]
            }
          };
        });

        placesMapRef.current = newPlacesMap;
        setSuggestionList(formatted);
        setSuggestionDescriptions(formatted.map(r => r.description));
      } catch {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
      }
    }, 300)
  );

  const handleInput = e => {
    const value = e?.target?.value ?? e;
    setInputValue(value);
    requestSuggestions.current(value);
  };

  const setSelectedValue = placeSelected => {
    setInputValue(placeSelected.description || placeSelected.name);
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};
    onPlaceSelected(placeSelected);
  };

  const buildPlace = (placeId, description, feature) => {
    const coordinates = feature?.geometry?.coordinates;
    if (!coordinates) return null;
    const [lng, lat] = coordinates;

    return {
      placeId,
      cityId: extractCityIdFromFeature(feature),
      lat,
      lng,
      name: description
    };
  };

  const handleSelect = (placeId, description) => async () => {
    if (!placeId) return;
    const feature = placesMapRef.current[description];
    if (!feature) return;

    const place = buildPlace(placeId, description, feature);
    if (place) setSelectedValue(place);
  };

  const handlePlaceSelected = async selectedDescription => {
    const feature = placesMapRef.current[selectedDescription];
    if (!feature?.id) return;

    const place = buildPlace(feature.id, selectedDescription, feature);
    if (!place) return;

    setInputValue(selectedDescription);
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};
    onPlaceSelected(place);
  };

  const handleInputChange = handleInput;

  return {
    handleInput,
    handleInputChange,
    suggestionList,
    suggestionDescriptions,
    inputValue,
    placesRef,
    handleSelect,
    handlePlaceSelected,
    placesMapRef
  };
};

export default useAutocompleteService;
